import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { copyToClipboardWithConfirmation } from '@openx/utils/lib/copyToClipboard.ts';
import { useSnackbar } from 'notistack';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../Button/Button.tsx';
import type { BulkAddValidationResult } from './ListBulkAddFooter.tsx';
import { StyledAlert, StyledBox, StyledErrorsStack, StyledTypography } from './ListBulkAddFooterError.styled.ts';

export const ListBulkAddFooterError = <T,>({
  error,
  rejectedItems,
}: Pick<BulkAddValidationResult<T>, 'error' | 'rejectedItems'>) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const buttonText = isExpanded ? t('Hide Details') : t('See Details');
  const itemsText = rejectedItems.length !== 1 ? t('items were') : t('item was');

  const rejectedItemsString = useMemo<string>(() => rejectedItems.join('\n'), [rejectedItems]);

  const handleAlertToggle = () => setIsExpanded(prevState => !prevState);

  return (
    <Box>
      <StyledAlert
        action={
          <Button data-test="error-details-button" color="error" onClick={handleAlertToggle} variant="text">
            {buttonText}
          </Button>
        }
        severity="error"
      >
        <StyledTypography data-test="validation-error-info">
          {t('{count} {itemsText} removed', {
            count: rejectedItems.length,
            itemsText,
          })}
        </StyledTypography>
      </StyledAlert>

      {isExpanded && (
        <StyledBox>
          <Typography data-test="validation-error-details-title" color="error" variant="caption">
            {error}
          </Typography>

          <StyledErrorsStack>
            {rejectedItems.map((value, index) => (
              <Typography data-test="validation-error-details-item" key={`${index}-${value}`}>
                {value}
              </Typography>
            ))}
          </StyledErrorsStack>

          <Button
            type="button"
            onClick={() => copyToClipboardWithConfirmation(rejectedItemsString, enqueueSnackbar, t)}
          >
            {t('Copy to Clipboard')}
          </Button>
        </StyledBox>
      )}
    </Box>
  );
};
